import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { libraryPageSlot } from '@/lib/desktop-shell/library-page-slots';
import { useLibraryPage } from '@/lib/navigation';

/**
 * Where a library page (settings, plugins, artifacts) renders: over the workspace area, beside
 * the shell sidebar that shows its navigation, like an app's items. The workspace stays
 * mounted underneath (inert, see `WorkspaceDockLayout`).
 */
export function LibraryPageLayer() {
  const { open } = useLibraryPage();
  if (!open) return null;
  return (
    <div
      className="absolute inset-0 z-40 flex min-h-0 flex-col overflow-hidden bg-background"
      data-library-page-layer
      ref={libraryPageSlot.set}
    />
  );
}

/** Renders an open library page into the shell's `LibraryPageLayer`. */
export function LibraryPageContent({ children }: { children: ReactNode }) {
  const slot = libraryPageSlot.useElement();
  return slot === null ? null : createPortal(children, slot);
}

/** The open page's root: the shell's page layer, or Settings over the whole window. */
function pageRoots(): Element[] {
  return [
    ...document.querySelectorAll('[data-library-page-layer], body > main.desktop-shell'),
  ].filter((root) => root.closest('[inert]') === null);
}

/** The open page's title (its `h1`), once it has rendered. */
function libraryPageTitle(): HTMLElement | null {
  for (const root of pageRoots()) {
    const titles = [...root.querySelectorAll<HTMLElement>('h1')];
    const title = titles.find((heading) => heading.getClientRects().length > 0) ?? titles[0];
    if (title !== undefined) return title;
  }
  return null;
}

/** Focus is already somewhere in the page (a field it focuses itself). */
function focusInPage(): boolean {
  const active = document.activeElement;
  if (active === null || active === document.body) return false;
  return pageRoots().some((root) => root.contains(active));
}

/** Marks an element the app focuses itself (a page's title): it draws no focus box. */
export const PROGRAMMATIC_FOCUS = 'data-programmatic-focus';

/** How long a page may take to show its title (lazy data), in frames. */
const TITLE_WAIT_FRAMES = 90;

/**
 * Moves focus into a library page when it opens (to its title, so a screen reader reads where
 * it is and Tab continues in the page) and back to what opened it when it closes.
 */
export function useLibraryPageFocus(): void {
  const { open, pathname } = useLibraryPage();
  const openerRef = useRef<HTMLElement | null>(null);
  const shownRef = useRef<string | null>(null);
  const shown = open ? pathname : null;
  useEffect(() => {
    const previous = shownRef.current;
    shownRef.current = shown;
    if (shown === previous) return;
    // Cancelled before it ran (a re-run of the effect), it runs again then.
    const undo = () => {
      shownRef.current = previous;
    };
    if (shown === null) {
      const opener = openerRef.current;
      // The workspace is interactive again from this commit on.
      const frame = requestAnimationFrame(() => {
        openerRef.current = null;
        if (opener?.isConnected && opener.closest('[inert]') === null)
          opener.focus({ preventScroll: true });
      });
      return () => {
        cancelAnimationFrame(frame);
        undo();
      };
    }
    if (previous === null && openerRef.current === null) {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
    let frames = 0;
    let done = false;
    let frame = requestAnimationFrame(function focusTitle() {
      const title = focusInPage() ? null : libraryPageTitle();
      if (title !== null) {
        if (!title.hasAttribute('tabindex')) title.tabIndex = -1;
        // Focused for a screen reader and for Tab to go on from there, not to be drawn: no box
        // around the page's title (globals.css). Tab never stops on it, so no keyboard ring lost.
        title.setAttribute(PROGRAMMATIC_FOCUS, '');
        title.focus({ preventScroll: true });
      }
      frames += 1;
      if (title !== null || focusInPage() || frames >= TITLE_WAIT_FRAMES) done = true;
      else frame = requestAnimationFrame(focusTitle);
    });
    return () => {
      cancelAnimationFrame(frame);
      if (!done) undo();
    };
  }, [shown]);
}
